import React, { useEffect, useState, useCallback } from "react";
import PageHeader from "../components/PageHeader";
import "../styles/StockPage.css";
import { fetchStockCategories, fetchStockItems, submitOnlineOrder } from "../services/api";

// Reused verbatim from the admin-dashboard's salesman-restricted Stock view
// (frontend/src/pages/Stock.jsx) so the availability result a dealer sees
// here always matches what a salesman sees internally.
const BUFFER_MULTIPLIER = 1.5; // 50% buffer — requested qty × 1.5

// E-catalogue links are mapped once at catalogue/file level. Items inherit the
// link from any catalogue they belong to.
const E_CATALOGUE_LINKS = {
  "jacquard24": "https://drive.google.com/file/d/1zB6i3UVs3FesBYjtR7VZT8bKWjad99l7/view",
  "madhubani": "https://drive.google.com/file/d/1gvYrX7B9mQXBFu30mmuJgXRu-q3D-pJn/view",
  "tanjore": "https://drive.google.com/file/d/1_hKx8ya3eCgK6vcMwNI5dAO9bJNxEdsY/view",
  "organic": "https://drive.google.com/file/d/1-YIJmNpj9Ng1r1V3H08OkhxpINJZ-OoN/view",
  "rivera": "https://drive.google.com/file/d/1pID7YafreunSgXdDFQqvZcGL79WxuenZ/view",
  "textura": "https://drive.google.com/file/d/1f2QJLEB5YCuVv1MQGynw-b9fQ9_RnRWL/view",
  "vaticanhanger": "https://drive.google.com/file/d/1KATbvk58jQAJHQ_WsV5DN8x75w3dQM1f/view",
  "fiber": "https://drive.google.com/file/d/1Nr2E61t0FaRAe4_K6J2R4TZrQScXsmN3/view",
  "willow": "https://drive.google.com/file/d/153-trxSSsu9ttmKIZ3CRourotigf8E-q/view",
  "sage": "https://drive.google.com/file/d/15jbem52n9YAzH4mTH1XaBtupWD76F9jT/view",
  "forest": "https://drive.google.com/file/d/12LPR9yhbz_CL08bp33Y8iMxNKn37Kgx7/view",
  "edit": "https://drive.google.com/file/d/1__9NM178UMOLFU8QQEdGIbWamH7bUr8d/view",
  "pebbles": "https://drive.google.com/file/d/1gmOrhQCQXoCeNswJd4RdndHv01Kc2IOy/view",
  "shell": "https://drive.google.com/file/d/1UZx2q5XgOz4zWuzJ1DXpLr1Kl4wZITkY/view",
  "infinity": "https://drive.google.com/file/d/1f29nk5h4FRlhAiyM_wl5OFeLSvezQyvd/view",
  "paris": "https://drive.google.com/file/d/1HI-8XISZfXQZmY4grcYGB8jTwZ6Bo3df/view",
  "jacquard40": "https://drive.google.com/file/d/1IZJvzdMJCVySuoGTE2Q7nMZk1ggSQtdW/view",
  "mexico": "https://drive.google.com/file/d/1MgYnhKXERaLDzUwQMrEgIHtLAby9MZCd/view",
  "union": "https://drive.google.com/file/d/11DpDXLDvvKKYDLoDvhL-pd_doO7976a6/view",
  "fusion": "https://drive.google.com/file/d/1tRdO9fj_leXS9oBKPrk2vCjzMtnZxrdt/view",
  "herringbone": "https://drive.google.com/file/d/1ypkMcu5_ChuRpDYSKBgg9AhhJGABDPVn/view",
  "maximafresco": "https://drive.google.com/file/d/1xZvpKouL3wkIc0rYjEhm1lgyiUqUT3TP/view",
  "jacquard18": "https://drive.google.com/file/d/12xUV4twyJg58emHKV-3OpU9tuXJ3nsMS/view",
  "sunblock": "https://drive.google.com/file/d/117G9L-3uXqHTkYZlutqwqxN8TuHtZ8Do/view",
  "texturaweaves": "https://drive.google.com/file/d/1k4dQOzPAHl0DB9ZGvkqaUKlfN0czZM92/view",
  "theatre": "https://drive.google.com/file/d/1Y5a4QLqLKPS-CZ0n0BKj5TuTgq4jm6J4/view",
  "elfin": "https://drive.google.com/file/d/1sHRBxFDREZzJ3kbim-TA6-KC4YJlELcw/view",
};

function catalogueKey(value) {
  return String(value || "").trim().toLowerCase().replace(/[^a-z0-9]/g, "");
}

function getECatalogues(catalogues = []) {
  return catalogues
    .map((name) => ({
      name,
      url: E_CATALOGUE_LINKS[catalogueKey(name)],
    }))
    .filter((entry) => entry.url);
}

function computeAvailability(item, requestedQty) {
  const qty = Number(requestedQty);
  if (!Number.isFinite(qty) || qty <= 0) return null;

  const threshold = qty * BUFFER_MULTIPLIER;
  const locations = [
    Number(item.panipat) || 0,
    Number(item.barhi) || 0,
    Number(item.delhi) || 0,
    Number(item.processHouse) || 0,
  ];

  const anySingleLocationMeetsThreshold = locations.some((v) => v >= threshold);
  if (anySingleLocationMeetsThreshold) return "available";

  const total = locations.reduce((sum, v) => sum + v, 0);
  if (total >= threshold) return "multiple";

  return "contact";
}

function formatPriceUpdatedAt(value) {
  if (!value) return "";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "";
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

const AVAILABILITY_DISPLAY = {
  available: { text: "Available", tone: "success" },
  multiple: {
    text: "Available at multiple locations — confirm with dispatch team",
    tone: "warning",
  },
  contact: { text: "Contact dispatch team", tone: "danger" },
};

function StockItemCard({ item, pricing }) {
  const [qtyInput, setQtyInput] = useState("");
  const [checkedQty, setCheckedQty] = useState("");
  const [orderSubmitting, setOrderSubmitting] = useState(false);
  const [orderSent, setOrderSent] = useState(false);
  const [orderFeedback, setOrderFeedback] = useState("");
  const [transportMode, setTransportMode] = useState("");
  const [remarks, setRemarks] = useState("");

  const handleQtyChange = (e) => {
    setQtyInput(e.target.value.replace(/[^\d.]/g, ""));
  };

  const handleCheck = () => {
    setCheckedQty(qtyInput);
    setOrderSent(false);
    setOrderFeedback("");
  };

  const handlePlaceOrder = async () => {
    if (!item.itemUniqueCode || !checkedQty || orderSubmitting || orderSent) return;
    setOrderSubmitting(true);
    setOrderFeedback("");
    try {
      const response = await submitOnlineOrder({
        itemUniqueCode: item.itemUniqueCode,
        quantity: Number(checkedQty),
        transportMode,
        remarks: remarks.trim(),
      });
      setOrderSent(true);
      const reference = response.order?.orderNumber || response.order?.id || "received";
      setOrderFeedback(`Order request recorded. Reference: ${reference}.`);
    } catch (error) {
      setOrderFeedback(error.message || "Unable to submit this order request. Please try again.");
    } finally {
      setOrderSubmitting(false);
    }
  };

  const result = checkedQty ? computeAvailability(item, checkedQty) : null;
  const display = result ? AVAILABILITY_DISPLAY[result] : null;

  return (
    <div className="stock-item-card">
      <div className="stock-item-name">{item.itemName}</div>
      {item.catalogues.length > 0 && (
        <div className="stock-item-catalogues">{item.catalogues.join(", ")}</div>
      )}
      {getECatalogues(item.catalogues).length > 0 && (
        <div className="stock-ecatalogue-actions">
          {getECatalogues(item.catalogues).map((catalogue) => (
            <a
              key={catalogue.name}
              className="stock-ecatalogue-button"
              href={catalogue.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              View E-Catalogue{getECatalogues(item.catalogues).length > 1 ? ` — ${catalogue.name}` : ""}
            </a>
          ))}
        </div>
      )}

      {pricing?.visible && (
        <div className="stock-price-panel">
          <div className="stock-price-row">
            <span className="stock-price-label">
              {pricing.label || "Cut Price"}
            </span>
            {item.regularPriceAvailable && item.regularCutPrice != null ? (
              <span className="stock-price-value">
                ₹{Number(item.regularCutPrice).toLocaleString("en-IN")}
              </span>
            ) : item.regularPriceNeedsReview ? (
              <span className="stock-price-unavailable">Contact ABN for price</span>
            ) : (
              <span className="stock-price-unavailable">Price not mapped</span>
            )}
          </div>

          {item.specialPriceAvailable && item.specialCutPrice != null && (
            <div className="stock-price-row stock-price-row--special">
              <span className="stock-price-label">
                {pricing.specialLabel || "Special Party Price"}
              </span>
              <span className="stock-price-value stock-price-value--special">
                ₹{Number(item.specialCutPrice).toLocaleString("en-IN")}
              </span>
            </div>
          )}

          {item.specialPriceNeedsReview && (
            <div className="stock-price-row stock-price-row--special">
              <span className="stock-price-label">
                {pricing.specialLabel || "Special Party Price"}
              </span>
              <span className="stock-price-unavailable">Contact ABN for price</span>
            </div>
          )}

          {item.lastUpdatedAt && (
            <div className="stock-price-updated">
              Last updated: {formatPriceUpdatedAt(item.lastUpdatedAt)}
            </div>
          )}
        </div>
      )}

      <div className="stock-card-inputs">
        <div className="stock-input-group">
          <label className="stock-input-label">Qty needed (mtrs)</label>
          <input
            className="stock-qty-input"
            type="text"
            inputMode="decimal"
            placeholder="e.g. 100"
            value={qtyInput}
            onChange={handleQtyChange}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCheck();
            }}
          />
        </div>
        <button className="stock-check-button" onClick={handleCheck}>
          Check
        </button>
      </div>

      {display && (
        <>
          <div className={`stock-availability-result stock-availability-${display.tone}`}>
            {display.text}
          </div>
          <p className="stock-order-hint">
            You can place an order request even when the item is currently unavailable.
          </p>
          <div className="stock-order-fields">
            <div className="stock-order-field">
              <label htmlFor={`transport-mode-${item.itemUniqueCode}`}>Transport mode <span aria-hidden="true">*</span></label>
              <select
                id={`transport-mode-${item.itemUniqueCode}`}
                value={transportMode}
                onChange={(event) => setTransportMode(event.target.value)}
                required
              >
                <option value="">Select transport mode</option>
                <option value="Transporter">Transporter</option>
                <option value="Bus">Bus</option>
                <option value="Courier">Courier</option>
                <option value="Self Pickup">Self Pickup</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="stock-order-field">
              <label htmlFor={`order-remarks-${item.itemUniqueCode}`}>Remarks / special instructions</label>
              <textarea
                id={`order-remarks-${item.itemUniqueCode}`}
                rows="3"
                maxLength="1000"
                placeholder="Add any delivery or packing instructions (optional)"
                value={remarks}
                onChange={(event) => setRemarks(event.target.value)}
              />
            </div>
          </div>
          <button
            type="button"
            className="stock-order-button"
            onClick={handlePlaceOrder}
            disabled={orderSubmitting || orderSent || !item.itemUniqueCode || !transportMode}
          >
            {orderSubmitting ? "Sending…" : orderSent ? "Request sent" : "Place order"}
          </button>
          {orderFeedback && (
            <p className={`stock-order-feedback${orderSent ? " is-success" : " is-error"}`} role="status">
              {orderFeedback}
            </p>
          )}
        </>
      )}
    </div>
  );
}

function StockPage() {
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [itemSearch, setItemSearch] = useState("");
  const [pricing, setPricing] = useState({ visible: false, state: "", regional: false, label: "Cut Price" });

  const loadStock = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const [categoriesResponse, itemsResponse] = await Promise.all([
        fetchStockCategories(),
        fetchStockItems(),
      ]);
      setCategories(categoriesResponse.categories || []);
      setItems(itemsResponse.items || []);
      setPricing(itemsResponse.pricing || { visible: false, state: "", regional: false, label: "Cut Price" });
    } catch (err) {
      setError("Unable to load stock data. Tap to retry.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStock();
  }, [loadStock]);

  const searchQ = itemSearch.trim().toLowerCase();
  const hasFilter = Boolean(selectedCategory || searchQ);

  const filteredItems = hasFilter
    ? items
        .filter((item) => !selectedCategory || item.catalogues.includes(selectedCategory))
        .filter((item) => !searchQ || item.itemName.toLowerCase().includes(searchQ))
    : [];

  return (
    <div className="stock-page">
      <PageHeader
        title="Stock Enquiry / Place Order"
        actions={
          <button
            type="button"
            className="page-header-refresh"
            onClick={loadStock}
            disabled={loading}
            aria-label="Refresh stock data"
            title="Refresh data"
          >
            ↻
          </button>
        }
      />

      <main className="stock-content">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner" />
            <p className="loading-text">Loading stock…</p>
          </div>
        ) : error ? (
          <div className="error-state">
            <button className="retry-button" onClick={loadStock}>
              {error}
            </button>
          </div>
        ) : (
          <>
            <div className="stock-filters">
              <div className="stock-filter-field">
                <label htmlFor="stock-category">Category</label>
                <select
                  id="stock-category"
                  className="stock-category-select"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  <option value="">All categories</option>
                  {categories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>

              <div className="stock-filter-field">
                <label htmlFor="stock-search">Search item</label>
                <input
                  id="stock-search"
                  className="stock-search-input"
                  type="text"
                  placeholder="Search all items…"
                  value={itemSearch}
                  onChange={(e) => setItemSearch(e.target.value)}
                />
              </div>
            </div>

            {!hasFilter && (
              <p className="stock-hint">
                Select a category or search for an item to check availability and place an order.
              </p>
            )}

            {selectedCategory && E_CATALOGUE_LINKS[catalogueKey(selectedCategory)] && (
              <div className="stock-selected-ecatalogue">
                <a
                  className="stock-ecatalogue-button"
                  href={E_CATALOGUE_LINKS[catalogueKey(selectedCategory)]}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View {selectedCategory} E-Catalogue
                </a>
              </div>
            )}

            {hasFilter && filteredItems.length === 0 && (
              <p className="stock-hint">No items found for this filter.</p>
            )}

            {filteredItems.length > 0 && (
              <div className="stock-items-list">
                {filteredItems.map((item) => (
                  <StockItemCard key={item.itemName} item={item} pricing={pricing} />
                ))}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

export default StockPage;
